import { motion } from "framer-motion";
import { Network, Landmark, Handshake, Compass, Gauge } from "lucide-react";
import { SectionHead, Reveal } from "./Reveal";
import { WHY } from "../content";

const Card = ({ children, className = "", delay = 0, testid }) => (
  <Reveal delay={delay} className={`h-full ${className}`}>
    <motion.div
      whileHover={{ y: -4 }}
      transition={{ type: "spring", stiffness: 260, damping: 22 }}
      className="group relative h-full overflow-hidden rounded-md border border-white/10 bg-[#111519] p-6 lg:px-7 lg:py-6 transition-colors duration-500 hover:border-emerald-400/40"
      data-testid={testid}
    >
      <div className="pointer-events-none absolute -right-20 -top-20 h-56 w-56 rounded-full bg-emerald-500/0 blur-3xl transition-colors duration-700 group-hover:bg-emerald-500/15" />
      {children}
    </motion.div>
  </Reveal>
);

const TRIO_ICONS = [Landmark, Handshake, Compass];

export const WhyPluk = () => (
  <section id="why" className="relative py-24 lg:py-10 bg-[#0d1013] border-y border-white/10" data-testid="why-section">
    <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12">
      <SectionHead
        index="07"
        label="Why PLUK HR"
        title={<>The right combination of experience, approach <em className="text-emerald-400">and access.</em></>}
        className="lg:max-w-5xl"
        titleClass="lg:!text-5xl lg:!mt-4"
      />
      <div className="mt-12 lg:mt-6 grid grid-cols-1 gap-3 md:grid-cols-12">
        <Card className="md:col-span-7" testid="why-card-experience">
          <div className="flex h-full flex-col gap-6 sm:flex-row sm:items-end sm:gap-8">
            <div className="shrink-0">
              <p className="eyebrow">Founder-led</p>
              <p className="font-display mt-4 text-[6rem] lg:text-[7.5rem] leading-[0.8] pb-3 text-emerald-400">{WHY.hero.k}</p>
            </div>
            <div>
              <p className="font-display text-3xl leading-tight text-zinc-100">{WHY.hero.t}</p>
              <p className="mt-3 max-w-lg text-sm text-zinc-400 leading-relaxed">{WHY.hero.d}</p>
            </div>
          </div>
        </Card>
        <Card className="md:col-span-5" delay={0.08} testid="why-card-network">
          <div className="flex h-full flex-col justify-between gap-6">
            <Network className="h-8 w-8 text-emerald-400" strokeWidth={1.4} />
            <div>
              <p className="font-display text-3xl text-zinc-100">{WHY.network.t}</p>
              <p className="mt-3 text-zinc-400 leading-relaxed">{WHY.network.d}</p>
            </div>
          </div>
        </Card>
        {WHY.trio.map((c, i) => {
          const Icon = TRIO_ICONS[i];
          return (
            <Card key={c.t} className="md:col-span-4" delay={0.05 * i} testid={`why-card-${i}`}>
              <div className="flex h-full flex-col justify-between gap-6">
                <Icon className="h-7 w-7 text-emerald-400" strokeWidth={1.4} />
                <div>
                  <p className="font-display text-2xl text-zinc-100">{c.t}</p>
                  <p className="mt-3 text-sm text-zinc-400 leading-relaxed">{c.d}</p>
                </div>
              </div>
            </Card>
          );
        })}
        <Card className="md:col-span-12" testid="why-card-agile">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-12 md:items-center">
            <div className="md:col-span-5 flex items-start gap-5">
              <Gauge className="mt-1 h-7 w-7 shrink-0 text-emerald-400" strokeWidth={1.4} />
              <div>
                <p className="font-display text-2xl text-zinc-100">{WHY.wide.t}</p>
                <p className="mt-3 text-sm text-zinc-400 leading-relaxed">{WHY.wide.d}</p>
              </div>
            </div>
            <p className="md:col-span-7 font-display text-4xl sm:text-5xl italic leading-[1] text-zinc-100 md:text-right">
              Experience. Networks. <span className="text-emerald-400">Execution.</span>
            </p>
          </div>
        </Card>
      </div>
    </div>
  </section>
);
